import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';


const RegistrationForm = ({ onSubmit, isLoading = false }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    agreeToTerms: false,
    subscribeNewsletter: false,
    subscribePromotions: false
  });

  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Calculate password strength
    if (name === 'password') {
      calculatePasswordStrength(value);
    }
  };

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password?.length >= 8) strength++;
    if (/[A-Z]/?.test(password)) strength++;
    if (/[a-z]/?.test(password)) strength++;
    if (/[0-9]/?.test(password)) strength++;
    if (/[^A-Za-z0-9]/?.test(password)) strength++;
    setPasswordStrength(strength);
  };

  const getPasswordStrengthText = () => {
    const texts = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
    return texts?.[passwordStrength] || 'Very Weak';
  };

  const getPasswordStrengthColor = () => {
    const colors = ['bg-error', 'bg-warning', 'bg-warning', 'bg-success', 'bg-success'];
    return colors?.[passwordStrength] || 'bg-error';
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData?.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (formData?.phone && !/^\+?[\d\s\-\(\)]{10,}$/?.test(formData?.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData?.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm() && onSubmit) {
      onSubmit(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Name Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="First Name"
          type="text"
          name="firstName"
          value={formData?.firstName}
          onChange={handleInputChange}
          error={errors?.firstName}
          placeholder="Enter your first name"
          disabled={isLoading}
          required
        />
        <Input
          label="Last Name"
          type="text"
          name="lastName"
          value={formData?.lastName}
          onChange={handleInputChange}
          error={errors?.lastName}
          placeholder="Enter your last name"
          disabled={isLoading}
          required
        />
      </div>
      {/* Email */}
      <Input
        label="Email Address"
        type="email"
        name="email"
        value={formData?.email}
        onChange={handleInputChange}
        error={errors?.email}
        placeholder="Enter your email address"
        disabled={isLoading}
        required
      />
      {/* Password */}
      <div>
        <div className="relative">
          <Input
            label="Password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            value={formData?.password}
            onChange={handleInputChange}
            error={errors?.password}
            placeholder="Create a strong password"
            disabled={isLoading}
            required
            className="pr-10"
          />
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-3 top-[34px] text-muted-foreground hover:text-foreground"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <Icon name={showPassword ? 'Eye' : 'EyeOff'} size={18} />
          </button>
        </div>
        {formData?.password && (
          <div className="mt-2">
            <div className="flex items-center space-x-2 mb-1">
              <div className="flex-1 bg-muted rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                  style={{ width: `${(passwordStrength / 5) * 100}%` }}
                />
              </div>
              <span className="text-xs text-muted-foreground">
                {getPasswordStrengthText()}
              </span>
            </div>
            <div className="text-xs text-muted-foreground">
              Password should contain uppercase, lowercase, numbers, and special characters
            </div>
          </div>
        )}
      </div>
      {/* Confirm Password */}
      <div className="relative">
        <Input
          label="Confirm Password"
          type={showConfirm ? 'text' : 'password'}
          name="confirmPassword"
          value={formData?.confirmPassword}
          onChange={handleInputChange}
          error={errors?.confirmPassword}
          placeholder="Confirm your password"
          disabled={isLoading}
          required
          className="pr-10"
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShowConfirm((v) => !v)}
          className="absolute right-3 top-[34px] text-muted-foreground hover:text-foreground"
          aria-label={showConfirm ? 'Hide password' : 'Show password'}
        >
          <Icon name={showConfirm ? 'Eye' : 'EyeOff'} size={18} />
        </button>
      </div>
      {/* Phone Number */}
      <Input
        label="Phone Number (Optional)"
        type="tel"
        name="phone"
        value={formData?.phone}
        onChange={handleInputChange}
        error={errors?.phone}
        placeholder="Enter your phone number"
        description="We'll use this for order notifications and updates"
        disabled={isLoading}
      />
      {/* Preferences */}
      <div className="space-y-4">
        <div className="border-t border-border pt-4">
          <h3 className="text-sm font-medium text-foreground mb-3">Preferences</h3>
          
          <div className="space-y-3">
            <Checkbox
              label="Subscribe to style tips newsletter"
              description="Get weekly style tips, tutorials, and product recommendations"
              checked={formData?.subscribeNewsletter}
              onChange={(e) => setFormData(prev => ({ ...prev, subscribeNewsletter: e?.target?.checked }))}
              disabled={isLoading}
            />
            
            <Checkbox
              label="Receive promotional offers"
              description="Be the first to know about sales, new arrivals, and exclusive deals"
              checked={formData?.subscribePromotions}
              onChange={(e) => setFormData(prev => ({ ...prev, subscribePromotions: e?.target?.checked }))}
              disabled={isLoading}
            />
          </div>
        </div>
      </div>
      {/* Terms Agreement */}
      <div className="border-t border-border pt-4">
        <Checkbox
          label="I agree to the Terms of Service and Privacy Policy"
          description="By creating an account, you agree to our terms and privacy practices"
          error={errors?.agreeToTerms}
          checked={formData?.agreeToTerms}
          onChange={(e) => setFormData(prev => ({ ...prev, agreeToTerms: e?.target?.checked }))}
          disabled={isLoading}
          required
        />
      </div>
      {/* Submit Button */}
      <Button
        type="submit"
        fullWidth
        loading={isLoading}
        iconName="UserPlus"
        iconPosition="left"
        className="mt-8"
      >
        Create Account
      </Button>
    </form>
  );
};

export default RegistrationForm;